import React from 'react'
import {Lang} from 'yougile-lang'
import {DataObjectId, Department, getParentDepartments} from 'yougile-model'
import {DepartmentsClient} from '@client/departments-client'
import {PermissionsChecker} from '@client/permissions-checker'
import {Sync} from '@client/sync'
import {showDeleteConfirm} from '@components/common/base/notifications'
import {LinkAdd} from '@components/dashboard/common/link-add/link-add'
import {DepartmentEditorWindow} from '@components/departments-edit/department-editor'
import {MessageBox} from '@components/message-box'
import {globals} from '@extensions/globals-base'

interface Props {
  edited: DataObjectId | 'new' | undefined
  setEdited: React.Dispatch<React.SetStateAction<DataObjectId | 'new'>>
}

export const DepartmentEditorCall = ({edited, setEdited}: Props) => {
  const box = Sync.getCurrentSandbox()
  const isAdmin = PermissionsChecker.isAdmin()
  const userId = globals.currentUserId
  const allUsers = box.getAllUsers(false).map(u => u.id)
  const departments = DepartmentsClient.listDepartments(box)
  const open = departments.find(d => d.id === edited)
  const close = () => setEdited(undefined)
  const save = (d: Department) => {
    const box = Sync.getCurrentSandbox()
    DepartmentsClient.updateDepartment(box, d)
    close()
  }
  const remove = async (d: Department) => {
    const ch = departments.find(c => c.parentId === d.id)
    if (ch) {
      MessageBox.show(Lang.msg.companies.cantDeleteWithChildren, 'warning')
      return
    }
    if (await showDeleteConfirm(Lang.msg.companies.depDeleteConfirm)) {
      const box = Sync.getCurrentSandbox()
      DepartmentsClient.remove(box, d)
      close()
    }
  }

  const isManager = !!departments.find(d => d.managers.includes(userId))

  const canEdit = (dep: Department) =>
    isAdmin ||
    (dep &&
      (dep.managers.includes(userId) ||
        !!getParentDepartments(dep, departments).find(
          d => d?.managers?.includes(userId)
        )))

  return (
    <>
      {(isAdmin || isManager) && (
        <LinkAdd
          title={Lang.msg.companies.addDepartment}
          handler={() => setEdited('new')}
        />
      )}
      {edited && (
        <DepartmentEditorWindow
          allDepartments={departments}
          userId={userId}
          isAdmin={isAdmin}
          department={open}
          close={close}
          save={save}
          remove={remove}
          allUsers={allUsers}
          canEdit={edited === 'new' || canEdit(open)}
        />
      )}
    </>
  )
}
